<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import UserService from '@/service/user.service';
import RequestService from '@/service/request.service';
import LoadingSpinner from '@/components/admin-dashboard/LoadingSpinner.vue';
import { formatFirebaseTimestamp } from '@/helpers';

type PartnerRow = {
  userId: string;
  fullName: string;
  email: string;
  referralCode?: string | null;
  totalLeads: number;
  totalRequests: number;
  paidRequests: number;
  unpaidRequests: number;
  referralAmount: number; // derived
  leads: any[];
  requests: any[];
};

const loading = ref(true);
const partners = ref<PartnerRow[]>([]);
const search = ref('');
const referralCommissionRate = 0.1; // 10% of request amount if available

function safeDate(date: any) {
  if (!date) return '—';
  if (date.toDate) return date.toDate();
  if (date.seconds) return new Date(date.seconds * 1000);
  return new Date(date);
}

async function loadData() {
  loading.value = true;
  const [partnerUsers, referralsMap] = await Promise.all([
    UserService.getPartners(),
    UserService.getAllReferralsMap(),
  ]);

  const allPartnerIds = partnerUsers.map((p: any) => p.userId);

  const partnerLeadsResults = await Promise.all(
    allPartnerIds.map((pid: string) => UserService.getUsersReferredBy(pid)),
  );

  const partnerRequestsResults = await Promise.all(
    partnerLeadsResults.map((leads: any[]) => RequestService.getRequestsByUserIdsWithDerived(leads.map((l: any) => l.userId))),
  );

  partners.value = partnerUsers.map((p: any, index: number) => {
    const leads = partnerLeadsResults[index] || [];
    const requests = partnerRequestsResults[index] || [];
    const paid = requests.filter((r: any) => r.isPaid);
    const unpaid = requests.length - paid.length;

    // Derive referral amount: sum of (amount * rate) if available, else per-paid flat amount
    const amountSum = requests.reduce((acc: number, r: any) => {
      const isPaid = r.isPaid;
      // eslint-disable-next-line no-nested-ternary
      const amount = typeof r.total === 'number' ? r.total : (typeof r.amount === 'number' ? r.amount : 0);
      if (isPaid && amount > 0) return acc + amount * referralCommissionRate;
      return acc;
    }, 0);

    const referralAmount = Math.round((amountSum + (paid.length && amountSum === 0 ? paid.length * 5 : 0)) * 100) / 100; // fallback £5 per paid if no amounts

    return {
      userId: p.userId,
      fullName: p.fullName || p.name || '—',
      email: p.email || '—',
      referralCode: referralsMap[p.userId] || null,
      totalLeads: leads.length,
      totalRequests: requests.length,
      paidRequests: paid.length,
      unpaidRequests: unpaid,
      referralAmount,
      leads,
      requests,
    } as PartnerRow;
  });

  loading.value = false;
}

const filteredPartners = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return partners.value;
  return partners.value.filter((p) => p.fullName.toLowerCase().includes(term)
    || p.email.toLowerCase().includes(term)
    || (p.referralCode || '').toLowerCase().includes(term));
});

onMounted(loadData);
</script>

<template>
  <div class="min-h-screen bg-black-primary text-white font-inter p-6">
    <div class="max-w-7xl mx-auto">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-gold mb-2">Partners Overview</h1>
          <p class="text-lg text-gray-300">Overview of partner performance and referrals.</p>
        </div>
        <img src="@/assets/gold-logo-transparent.png" alt="TALAB Logo" class="w-24 md:w-32" />
      </div>

      <div class="mb-6 flex items-center gap-3">
        <input v-model="search" placeholder="Search partners..." class="px-4 py-2 rounded-lg bg-black-secondary border border-zinc-700 focus:outline-none focus:border-gold-primary w-full md:w-80" />
      </div>

      <div v-if="loading" class="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>

      <div v-else class="space-y-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="p in filteredPartners" :key="p.userId" class="bg-neutral-800 rounded-2xl p-6 shadow-lg">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="text-xl font-semibold text-gold">{{ p.fullName }}</h3>
                <p class="text-sm text-gray-400">{{ p.email }}</p>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold">£{{ p.referralAmount.toFixed(2) }}</div>
                <div class="text-xs text-gray-400">Referral Amount</div>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3 text-sm">
              <div class="bg-black-primary/60 rounded-lg p-3">
                <div class="text-2xl font-bold">{{ p.totalLeads }}</div>
                <div class="text-xs text-gray-400">Leads</div>
              </div>
              <div class="bg-black-primary/60 rounded-lg p-3">
                <div class="text-2xl font-bold">{{ p.totalRequests }}</div>
                <div class="text-xs text-gray-400">Requests</div>
              </div>
              <div class="bg-black-primary/60 rounded-lg p-3">
                <div class="text-2xl font-bold text-green-400">{{ p.paidRequests }}</div>
                <div class="text-xs text-gray-400">Paid</div>
              </div>
              <div class="bg-black-primary/60 rounded-lg p-3">
                <div class="text-2xl font-bold text-yellow-400">{{ p.unpaidRequests }}</div>
                <div class="text-xs text-gray-400">Unpaid</div>
              </div>
            </div>
            <div class="mt-4 text-sm">
              <div class="text-gray-400">Referral Code</div>
              <div class="font-mono tracking-widest text-white">{{ p.referralCode || 'N/A' }}</div>
            </div>
            <details class="mt-4">
              <summary class="cursor-pointer text-gold">View activity</summary>
              <div class="mt-3">
                <div class="text-sm text-gray-400 mb-2">Recent Leads</div>
                <div class="max-h-40 overflow-y-auto pr-1">
                  <table class="w-full text-left text-sm">
                    <thead><tr class="text-gold/90"><th class="py-1">Name</th><th class="py-1">Email</th><th class="py-1">Registered</th></tr></thead>
                    <tbody>
                      <tr v-for="l in p.leads" :key="l.userId" class="border-b border-zinc-700/50">
                        <td class="py-1">{{ l.fullName }}</td>
                        <td class="py-1">{{ l.email }}</td>
                        <td class="py-1">{{ formatFirebaseTimestamp(l.registrationDate, true) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="text-sm text-gray-400 mt-4 mb-2">Recent Requests</div>
                <div class="max-h-40 overflow-y-auto pr-1">
                  <table class="w-full text-left text-sm">
                    <thead><tr class="text-gold/90"><th class="py-1">Lead</th><th class="py-1">Service</th><th class="py-1">Date</th><th class="py-1">Status</th></tr></thead>
                    <tbody>
                      <tr v-for="r in p.requests" :key="r.id" class="border-b border-zinc-700/50">
                        <td class="py-1">{{ r.userId }}</td>
                        <td class="py-1">{{ r.title || r.service || '—' }}</td>
                        <td class="py-1">{{ safeDate(r.createdAt).toLocaleDateString() }}</td>
                        <td class="py-1"><span :class="r.isPaid ? 'text-green-400' : 'text-yellow-400'">{{ r.isPaid ? 'paid' : (r.status || 'pending') }}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-inter { font-family: "Inter", sans-serif; }
.text-gold { @apply text-gold-primary; }
.bg-black-primary { background: #181818; }
.bg-black-secondary { background: #0C0C0C; }
</style>
